import React, { useState } from 'react';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError('');

    // Simulate API call delay
    setTimeout(() => {
      // Simple validation - in real app, this would be API call
      if (formData.email.includes('@') && formData.password.length >= 6) {
        onLogin({
          email: formData.email,
          role: 'viewer', // Default role
          name: formData.email.split('@')[0]
        });
      } else {
        setError('Invalid email or password (min 6 characters)');
      }
      setIsLoading(false);
    }, 1000);
  };

  const quickLogin = (role, email = null) => {
    const userData = {
      email: email || `${role}@company.com`,
      role: role,
      name: role.charAt(0).toUpperCase() + role.slice(1)
    };
    onLogin(userData);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden transition-colors duration-300" style={{
      backgroundImage: 'url(/data1.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}>
      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-black bg-opacity-5 dark:bg-opacity-10"></div>
      
      {/* Enhanced diagonal white section on the right */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-gray-100 opacity-100" style={{
        clipPath: 'polygon(55% 0%, 100% 0%, 100% 100%, 65% 100%)'
      }}></div>
      
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-10" style={{
        clipPath: 'polygon(55% 0%, 100% 0%, 100% 100%, 65% 100%)',
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0,0,0,0.15) 1px, transparent 0)`,
        backgroundSize: '20px 20px'
      }}></div>
      
      {/* Soft glow effect */}
      <div className="absolute inset-0 bg-gradient-to-l from-blue-50 via-transparent to-transparent opacity-30" style={{
        clipPath: 'polygon(55% 0%, 100% 0%, 100% 100%, 65% 100%)'
      }}></div>
      
      {/* Enhanced border highlight */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-200 via-white to-gray-200 opacity-60" style={{
        clipPath: 'polygon(54% 0%, 55% 0%, 65% 100%, 64% 100%)'
      }}></div>

      {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white bg-opacity-10 dark:bg-blue-400 dark:bg-opacity-15 rounded-full mix-blend-overlay filter blur-xl animate-pulse-soft"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white bg-opacity-10 dark:bg-purple-400 dark:bg-opacity-15 rounded-full mix-blend-overlay filter blur-xl animate-pulse-soft animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white bg-opacity-5 dark:bg-indigo-400 dark:bg-opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-pulse-soft animation-delay-4000"></div>
        </div>

      <div className="relative z-10 w-full max-w-md ml-auto mr-16 -translate-x-16">
        {/* Logo above login form */}
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="Data Bing Bong" 
            className="h-16 mx-auto"
          />
        </div>

        {/* Login Form */}
        <div className="bg-gray-50 bg-opacity-90 backdrop-blur-xl rounded-3xl p-10 shadow-2xl border border-gray-200 dark:bg-gray-100 dark:bg-opacity-95 dark:border-gray-300 transform transition-all duration-500 hover:shadow-3xl hover:bg-opacity-95 dark:hover:bg-opacity-100">
          {/* Login heading */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Login</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-100 border border-red-300 text-red-700 px-5 py-4 rounded-xl text-sm backdrop-blur-sm">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            <div className="relative group">
              <label htmlFor="email" className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-3 transition-colors group-focus-within:text-blue-200">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-5 py-4 bg-white bg-opacity-25 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-60 focus:border-transparent text-gray-800 placeholder-gray-500 placeholder-opacity-60 backdrop-blur-md dark:bg-gray-200 dark:bg-opacity-70 dark:border-gray-400 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:ring-blue-400 dark:focus:ring-opacity-60 transition-all duration-300 hover:bg-opacity-30 dark:hover:bg-opacity-80"
                  placeholder="Enter your email"
                  disabled={isLoading}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-focus-within:opacity-20 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>

            <div className="relative group">
              <label htmlFor="password" className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-3 transition-colors group-focus-within:text-blue-200">
                Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-5 py-4 bg-white bg-opacity-25 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-60 focus:border-transparent text-gray-800 placeholder-gray-500 placeholder-opacity-60 backdrop-blur-md dark:bg-gray-200 dark:bg-opacity-70 dark:border-gray-400 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:ring-blue-400 dark:focus:ring-opacity-60 transition-all duration-300 hover:bg-opacity-30 dark:hover:bg-opacity-80"
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-focus-within:opacity-20 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 backdrop-blur-sm border border-gray-200 hover:border-opacity-40 disabled:cursor-not-allowed transform hover:scale-105 hover:shadow-lg active:scale-95 dark:from-blue-600 dark:to-purple-700 dark:hover:from-blue-700 dark:hover:to-purple-800"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-white border-opacity-30 border-t-white rounded-full animate-spin mr-3"></div>
                  <span className="animate-pulse">Signing In...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Sign In
                </div>
              )}
            </button>
          </form>

          {/* Quick Login Options */}
          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white bg-opacity-10 backdrop-blur-sm rounded-full text-gray-600 text-opacity-80 dark:text-gray-300 py-1">Quick Access</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4">
              <button
                onClick={() => quickLogin('viewer')}
                disabled={isLoading}
                className="group py-3 px-4 bg-white bg-opacity-15 hover:bg-opacity-25 text-gray-800 text-sm rounded-xl transition-all duration-300 backdrop-blur-sm border border-gray-200 hover:border-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-200 dark:bg-opacity-60 dark:hover:bg-opacity-80 dark:border-gray-300 dark:hover:border-opacity-80 transform hover:scale-105 active:scale-95"
              >
                <div className="flex flex-col items-center">
                  <svg className="w-5 h-5 mb-1 opacity-70 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Viewer
                </div>
              </button>
              <button
                onClick={() => quickLogin('admin')}
                disabled={isLoading}
                className="group py-3 px-4 bg-white bg-opacity-15 hover:bg-opacity-25 text-gray-800 text-sm rounded-xl transition-all duration-300 backdrop-blur-sm border border-gray-200 hover:border-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-200 dark:bg-opacity-60 dark:hover:bg-opacity-80 dark:border-gray-300 dark:hover:border-opacity-80 transform hover:scale-105 active:scale-95"
              >
                <div className="flex flex-col items-center">
                  <svg className="w-5 h-5 mb-1 opacity-70 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Admin
                </div>
              </button>
              <button
                onClick={() => quickLogin('manager')}
                disabled={isLoading}
                className="group py-3 px-4 bg-white bg-opacity-15 hover:bg-opacity-25 text-gray-800 text-sm rounded-xl transition-all duration-300 backdrop-blur-sm border border-gray-200 hover:border-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-200 dark:bg-opacity-60 dark:hover:bg-opacity-80 dark:border-gray-300 dark:hover:border-opacity-80 transform hover:scale-105 active:scale-95"
              >
                <div className="flex flex-col items-center">
                  <svg className="w-5 h-5 mb-1 opacity-70 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Manager
                </div>
              </button>
            </div>
          </div>


        </div>


      </div>
    </div>
  );
};

export default Login; 